// The Job Queue's task actions: ONE copy, used by the desktop Job Queue (pages/JobQueuePage.jsx)
// and the phone queue (mobile/screens/QueueScreen.jsx). docs/mobile-pwa-plan.md "One copy of every
// action": a screen never re-implements one of these, it passes its own `ui`.
//
// `ui` is how an action talks to the person (features/queue/desktopUi.js = the browser's own
// dialogs, exactly as the desktop page always did; mobile/ui/phoneUi.js = phone panels):
//   pin(message, opts)        -> PIN string | null          (utils/pinPrompt: a sheet on a phone)
//   alert(message)            -> (awaited)
//   confirm(message, opts)    -> boolean
//   prompt(message, default)  -> string | null
//   causePrompt(opts)         -> {cause_code, reason} | null (T&M defect cause)
//   remakeHoldChoice(hold)    -> 'ack' | 'release' | 'close_short' | 'wait'
//   remakeHeadsUp(preview, o) -> (awaited)
//   askTypedMinutes(detail)   -> minutes | null              (T&M Auto-Complete)
//   stickLots(state)          -> {lots} | {noTag} | null     (saw stick lot at Complete; absent = skip)
//   palletBeacons({taskId, data}) -> (awaited)               (Laser Start: the SO pallets + beacons; absent = skip)
//   openPdf(blob) | null      -> print / show a report; null = this screen doesn't print
//
// Every action resolves to {reload: boolean}: true when the job changed and the screen should
// re-read it. A completed Complete / Auto-Complete also carries `travelerUrl` (the report it
// prints, or would print: see travelerUrlFor). Busy state: `setBusy(true|false)` is called where the desktop page always set
// isSubmitting (the button lock); a double-tap guard (submittingRef) stays the caller's.
import config from "../../config.js";
import { detailToString } from "../../utils/apiError";
import { isTmMinutesRequired } from "../../utils/tmAutoComplete";
import { autoCompletePlan, fmtSeconds } from "../../utils/multiOpFlow";
import { withCause } from "../../utils/tmDefects";
import { isTmLinked } from "../../utils/tmPricing";
import { isSawStickTask, fetchStickLots, saveStickLots, shouldAskStickLot } from "../../utils/stickLots";
import { isConfirmableSheetRefusal } from "../../utils/remnantTracking";
import { surplusCompleteNote } from "../surplus/surplusBundles";

const API_BASE = config.API_BASE_URL;
const noop = () => {};
const RELOAD = Object.freeze({ reload: true });
const NO_RELOAD = Object.freeze({ reload: false });

/** The body of a failed response as a sentence. An object detail ({error, message}, e.g. the
 *  remake hold) reads as its message rather than as raw JSON. */
export function errorTextToMessage(text) {
  let msg = text;
  try {
    const asJson = JSON.parse(text);
    if (asJson && typeof asJson.detail === "string") {
      msg = asJson.detail;
    } else if (asJson && Array.isArray(asJson.detail)) {
      msg = asJson.detail.map((d) => d.msg || JSON.stringify(d)).join(" | ");
    } else if (asJson && asJson.detail && typeof asJson.detail === "object") {
      // {error, message} / {code, message} / {detail} — detailToString reads .message first.
      msg = detailToString(asJson.detail);
    }
    if (!msg) msg = "An error occurred.";
  } catch {
    // keep msg as text
  }
  return typeof msg === "string" ? msg : String(msg);
}

/** A non-OK Response as a human-readable string (plain text, {detail: "..."}, validation lists). */
export async function parseServerError(res) {
  return errorTextToMessage(await res.text());
}

const OPERATOR_PIN = "Enter your 4-digit PIN:";

function postJson(url, body) {
  return fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** What the manager is asked when POST /start answers 403 (the API names the block). */
export function startOverrideMessage(msg) {
  if (msg.includes("SCHEDULE BLOCK")) return `${msg}\n\nManager/Admin: Enter PIN to AUTHORIZE this operator:`;
  if (msg.includes("QUALITY LOCK")) return `${msg}\n\nManager/Admin: Enter PIN to AUTHORIZE unlock:`;
  if (msg.includes("PART OBSOLETE")) return `${msg}\n\nManager/Admin: Enter PIN to AUTHORIZE use:`;
  if (msg.includes("ACCESS DENIED")) return `${msg}\n\nAuthorized User Only.\nManager/Admin: Enter PIN to OVERRIDE and start:`;
  if (msg.includes("MAINTENANCE REQUIRED")) return `${msg}\n\nManager/Admin: Enter PIN to OVERRIDE maintenance block:`;
  return `Access Denied: ${msg}\n\nManager/Admin: Enter PIN to AUTHORIZE:`;
}

/** The "measure N pieces during the run" reminder after a Laser start, or null. */
export function ppapOngoingReminder(cutSheetParts) {
  const parts = Array.isArray(cutSheetParts) ? cutSheetParts.filter((p) => p && p.ppap_required) : [];
  if (!parts.length) return null;
  let msg = "⚠️ PPAP Ongoing Sampling Required ⚠️\n\n";
  parts.forEach((p) => {
    msg += `- ${p.product_id}: Measure ${p.ppap_sample_size_ongoing} piece(s) during the run.\n`;
  });
  return msg;
}

// ---------------------------------------------------------------------------------------------
// Start / Pause / Finish Op / Maintenance
// ---------------------------------------------------------------------------------------------

/**
 * Start (or resume) a task. A 403 is a block the API names (schedule, quality lock, obsolete part,
 * access, maintenance): a manager's PIN retries with both PINs. On the Laser: at least one sheet
 * must be added first, a task with a packaging plan prints its routing slip (`ui.openPdf`), the
 * sales-order pallets it fills are shown with their beacons (`ui.palletBeacons`, when the answer
 * carries `pallet_beacons.pallets`), and PPAP parts get the ongoing-sampling reminder.
 */
export async function startTask({ task, workstation, isMaintenanceTask = false, cutSheetParts = null, ui, setBusy = noop }) {
  if (workstation === "Laser" && task && task.sheet_required > 0 && (task.sheet_count || 0) < 1) {
    await ui.alert("Material validation required!\n\nYou must add at least 1 sheet using the 'Add Sheet' button before starting this job. This ensures the correct material has been verified.");
    return NO_RELOAD;
  }

  setBusy(true);
  const operatorPin = await ui.pin(OPERATOR_PIN);
  if (!operatorPin) {
    setBusy(false);
    return NO_RELOAD;
  }

  const performStart = async (adminOverridePin = null) => {
    const payload = { pin: operatorPin, punch_time_utc: new Date().toISOString() };
    if (adminOverridePin) payload.admin_override_pin = adminOverridePin;
    const res = await postJson(`${API_BASE}/tasks/${task.task_id}/start`, payload);
    if (!res.ok) {
      if (res.status === 403) {
        const msg = await parseServerError(res);
        // A SECOND person authorises here: face would just see the operator again (and loop on the 403).
        const adminPin = await ui.pin(startOverrideMessage(msg), { face: false });
        if (adminPin) return performStart(adminPin);
        return NO_RELOAD;
      }
      await ui.alert(await parseServerError(res));
      return NO_RELOAD;
    }
    let startBody = null;
    try { startBody = await res.json(); } catch (_) { /* non-fatal */ }
    if (startBody && startBody.drop_label_print_job_id) {
      console.log(
        `Drop labels queued at Start (#${startBody.drop_label_print_job_id}) — agent will print to ZP 505 within ~10s.`
      );
    }
    // The routing slip tells the operator which pallets / boxes to set up before the cut runs.
    // ?include=routing: the per-part Task Report pages belong on the Complete-time print.
    if (workstation === "Laser" && !isMaintenanceTask && startBody && startBody.has_routing_slip && ui.openPdf) {
      try {
        const slipRes = await fetch(`${API_BASE}/tasks/${task.task_id}/laser_report_only?include=routing`);
        if (slipRes.status === 200) {
          ui.openPdf(await slipRes.blob());
        } else {
          console.warn("Routing slip on start status:", slipRes.status);
        }
      } catch (slipErr) {
        console.error("Error fetching routing slip on start:", slipErr);
      }
    }
    // Sales-order pallet beacons (2026-10-22): the API names the pallets this task fills and lights
    // their beacons; the popup says which to get. Advisory: it never fails the start.
    if (workstation === "Laser" && startBody && startBody.pallet_beacons
        && Array.isArray(startBody.pallet_beacons.pallets) && startBody.pallet_beacons.pallets.length
        && typeof ui.palletBeacons === "function") {
      try {
        await ui.palletBeacons({ taskId: task.task_id, data: startBody.pallet_beacons });
      } catch (pbErr) {
        console.error("Pallet beacons popup:", pbErr);
      }
    }
    if (workstation === "Laser") {
      const reminder = ppapOngoingReminder(cutSheetParts);
      if (reminder) await ui.alert(reminder);
    }
    return RELOAD;
  };

  try {
    return await performStart();
  } catch (e) {
    await ui.alert(e.message || "Error starting task.");
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

export async function pauseTask({ task, ui, setBusy = noop }) {
  setBusy(true);
  const userPin = await ui.pin(OPERATOR_PIN);
  if (!userPin) {
    setBusy(false);
    return NO_RELOAD;
  }
  try {
    const res = await postJson(`${API_BASE}/tasks/${task.task_id}/pause`, { pin: userPin, punch_time_utc: new Date().toISOString() });
    if (!res.ok) {
      await ui.alert(await parseServerError(res));
      return NO_RELOAD;
    }
    return RELOAD;
  } catch (e) {
    await ui.alert(e.message || "Error pausing task.");
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

/**
 * Finish the CURRENT op of a multi-op task (Lathe / Milling) and hand it to the next one. Only
 * offered while current_op < op_count: the last op is finished with Complete. `opFlow` =
 * utils/multiOpFlow `multiOpFlow(task)`.
 */
export async function finishOp({ task, opFlow, ui, setBusy = noop }) {
  const { opCount, currentOp } = opFlow;
  const nextOp = currentOp + 1;
  // Paused: the time was banked by the pause; this only moves the cursor.
  const alreadyPaused = task && task.status === "Paused";
  // The op boundary is irreversible without an Admin PIN, and it is one button away from Pause
  // on the same card, so confirm with the real numbers in it.
  const ok = await ui.confirm(
    `Finish operation ${currentOp} of ${opCount}?\n\n` +
    (alreadyPaused
      ? `The task is paused, so your time on operation ${currentOp} is already banked. `
      : `Your time on operation ${currentOp} will be banked and `) +
    `the task will wait for operation ${nextOp}. This does NOT complete the task.`,
    { title: `Finish operation ${currentOp}`, confirmLabel: `Finish operation ${currentOp}` }
  );
  if (!ok) return { reload: false, cancelled: true };

  setBusy(true);
  const userPin = await ui.pin(OPERATOR_PIN);
  if (!userPin) {
    setBusy(false);
    return NO_RELOAD;
  }
  try {
    const res = await postJson(`${API_BASE}/tasks/${task.task_id}/finish_op`, { pin: userPin, punch_time_utc: new Date().toISOString() });
    if (!res.ok) {
      await ui.alert(await parseServerError(res));
      return NO_RELOAD;
    }
    const data = await res.json();
    await ui.alert(
      `${data.finished_op_label} finished.\n\n` +
      `Next up: ${data.next_op_label} (${data.ops_remaining} operation` +
      `${data.ops_remaining === 1 ? "" : "s"} remaining).\n` +
      `Total time so far: ${data.total_elapsed_time}`
    );
    return RELOAD;
  } catch (e) {
    await ui.alert(e.message || "Error finishing operation.");
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

export async function enterMaintenance({ task, ui, setBusy = noop }) {
  const reason = await ui.prompt("Enter the reason for maintenance (at least 5 characters):");
  if (!reason || reason.trim().length < 5) {
    await ui.alert("A valid maintenance reason is required.");
    return NO_RELOAD;
  }
  const pin = await ui.pin(OPERATOR_PIN);
  if (!pin) return NO_RELOAD;

  setBusy(true);
  try {
    const res = await postJson(`${API_BASE}/tasks/${task.task_id}/enter_maintenance`, {
      pin, reason: reason.trim(), punch_time_utc: new Date().toISOString(),
    });
    if (!res.ok) {
      await ui.alert(`Error entering maintenance mode: ${await parseServerError(res)}`);
      return NO_RELOAD;
    }
    return RELOAD;
  } catch (err) {
    await ui.alert(`An error occurred: ${err.message}`);
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

export async function exitMaintenance({ task, ui, setBusy = noop }) {
  const pin = await ui.pin(OPERATOR_PIN);
  if (!pin) return NO_RELOAD;

  setBusy(true);
  try {
    const res = await postJson(`${API_BASE}/tasks/${task.task_id}/exit_maintenance`, {
      pin, punch_time_utc: new Date().toISOString(),
    });
    if (!res.ok) {
      await ui.alert(`Error exiting maintenance mode: ${await parseServerError(res)}`);
      return NO_RELOAD;
    }
    return RELOAD;
  } catch (err) {
    await ui.alert(`An error occurred: ${err.message}`);
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

// ---------------------------------------------------------------------------------------------
// Complete / Auto-Complete (remake hold, T&M minutes, reports, labels)
// ---------------------------------------------------------------------------------------------

/**
 * Remake hold (jobq-api _remake_hold_gate, 2026-09-25). Completing a join set (Welding) that is
 * still waiting on replacement parts answers 409 {detail: {error: "remake_hold", level, message}}:
 *   "confirm" — the replacements are on a makeup job but JobQ doesn't show them ready yet; the
 *               operator says they are here (remake_ack).
 *   "block"   — no replacement job exists for them. A manager either RELEASES the set (made another
 *               way: it completes at its full count) or, where the yield policy allows it
 *               (can_close_short), CLOSES IT SHORT (POST /tasks/{id}/close_short).
 * Resolves to {extra} (fields for the retry), {closedShort} (retry plain unless the set was
 * voided), or null when the operator backs out.
 */
export async function resolveRemakeHold(hold, ui) {
  const reasonMin = hold.reason_min || 5;
  const action = await ui.remakeHoldChoice(hold);
  if (action === "ack") return { extra: { remake_ack: true } };
  if (action !== "release" && action !== "close_short") return null;
  const closing = action === "close_short";
  // Never auto-filled from an admin session: this PIN is the authorisation.
  const pin = await ui.pin(
    closing ? "Admin or Manager PIN to close this set short:" : "Admin or Manager PIN to release this set:",
    { autofill: false }
  );
  if (!pin) return null;
  const reason = await ui.prompt(
    closing
      ? `Why is this set being closed short? (at least ${reasonMin} characters)\n` +
        `e.g. "customer agreed to take 50"`
      : `Why is this set being completed without its replacement parts? (at least ${reasonMin} characters)\n` +
        `e.g. "spare used from job 9419" or "customer accepted 50"`,
    ""
  );
  const trimmed = (reason || "").trim();
  if (trimmed.length < reasonMin) {
    if (reason !== null) await ui.alert(`A reason of at least ${reasonMin} characters is needed. The set was not completed.`);
    return null;
  }
  if (!closing) return { extra: { remake_override_pin: pin, remake_override_reason: trimmed } };
  const res = await postJson(`${API_BASE}/tasks/${hold.task_id}/close_short`, { pin, reason: trimmed });
  const text = await res.text();
  if (!res.ok) {
    await ui.alert(`The set was not closed short: ${errorTextToMessage(text)}`);
    return null;
  }
  let result = null;
  try { result = JSON.parse(text); } catch { /* keep null */ }
  return { closedShort: result || { message: "Set closed short.", set_voided: false } };
}

/**
 * POST a task completion and answer a remake hold (409) with the operator or a manager, then
 * retry with the same body. Resolves to {res} on success, {cancelled: true} when the operator
 * backed out, or {error} with a readable message. Every answer carries `closedShort` when the
 * set was closed short on the way; a set closed to 0 was voided and is NOT completed
 * ({closedShort} alone). With opts.tmMinutes (Auto-Complete) a T&M task's typed actual minutes
 * ride on every retry.
 */
export async function postCompletion(url, body, opts = {}, ui) {
  let extra = {};
  let closedShort = null;
  let minutes = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    const res = await postJson(url, minutes == null ? { ...body, ...extra } : { ...body, ...extra, actual_minutes: minutes });
    if (res.ok) return minutes == null ? { res, closedShort } : { res, closedShort, typedMinutes: minutes };
    const text = await res.text();
    let detail = null;
    try { detail = JSON.parse(text)?.detail ?? null; } catch { /* not JSON */ }
    if (opts.tmMinutes && minutes == null && isTmMinutesRequired(res.status, detail)) {
      // A T&M task: Auto-Complete needs the real minutes (they are billed). Asking does not use
      // up an attempt; Cancel completes nothing.
      minutes = await ui.askTypedMinutes(detail);
      if (minutes == null) return { cancelled: true, closedShort };
      attempt--;
      continue;
    }
    if (res.status === 409 && detail && typeof detail === "object" && detail.error === "remake_hold") {
      const next = await resolveRemakeHold(detail, ui);
      if (!next) return { cancelled: true, closedShort };
      if (next.closedShort) {
        closedShort = next.closedShort;
        if (closedShort.set_voided) return { closedShort };
        extra = {};
        continue;
      }
      extra = next.extra;
      continue;
    }
    return { error: errorTextToMessage(text), closedShort };
  }
  return { error: "This set is still waiting on replacement parts. Nothing was completed.", closedShort };
}

/** The task report a completion prints, or null (a part-less, list-less task has none). */
export function taskReportUrl({ task, jobId, includeOverride }) {
  if (task.workstation === "Laser") {
    // 'routing' / 'report' / 'all' — which page types the laser report emits (default: all).
    const inc = (includeOverride === "routing" || includeOverride === "report" || includeOverride === "all")
      ? `?include=${includeOverride}`
      : "";
    return `${API_BASE}/tasks/${task.task_id}/laser_report_only${inc}`;
  }
  if (task.workstation === "Fabrication" && !task.part_id && task.cut_list_id) {
    // Cut-list-level task (saw Cutting, Lathe / Milling off the stick): one page per part.
    return `${API_BASE}/reports/cut_list_report/${task.task_id}`;
  }
  if (!task.part_id) return null;
  const station = (task.workstation === "Fabrication" && task.sub_process) ? task.sub_process : task.workstation;
  return `${API_BASE}/reports/job_task_report?job_id=${jobId}&part_id=${task.part_id}&current_station=${station}`;
}

/** GET a report PDF as a Blob. Throws Error(the API's detail, else the HTTP status). */
export async function fetchReportPdf(url) {
  const res = await fetch(url);
  if (!res.ok) {
    const errorData = await res.json();
    const errorMsg = errorData.detail ? JSON.stringify(errorData.detail) : `Failed to fetch PDF: ${res.status} ${res.statusText}`;
    throw new Error(errorMsg);
  }
  return res.blob();
}

/**
 * The report a Complete prints = the traveler that goes with the parts (its QR leads to the next
 * station's task). null for a maintenance task or a task with no linked part. A screen that
 * doesn't print (the phone) offers it instead: Complete / Auto-Complete return it as `travelerUrl`.
 */
export function travelerUrlFor({ task, jobId, isMaintenanceTask, includeOverride }) {
  if (isMaintenanceTask) return null;
  return taskReportUrl({ task, jobId, includeOverride });
}

/** Print the task's report (skipped for maintenance tasks and on screens that don't print). */
export async function printTaskReport({ task, jobId, isMaintenanceTask, includeOverride, ui }) {
  if (isMaintenanceTask) {
    console.log("Skipping report generation for Maintenance task.");
    return;
  }
  if (!ui.openPdf) return;
  try {
    const pdfUrl = taskReportUrl({ task, jobId, includeOverride });
    if (!pdfUrl) {
      await ui.alert("Cannot generate a report for this task as it has no linked part.");
      return;
    }
    ui.openPdf(await fetchReportPdf(pdfUrl));
  } catch (err) {
    await ui.alert("Error generating report: " + err.message);
  }
}

/** The answer of a completion with a set closed short on the way, as the desktop always said it. */
async function settleCompletionOutcome(out, ui) {
  if (out.closedShort && !out.res && !out.error && !out.cancelled) {
    // Closed short to zero: the set was voided, there is nothing left to complete.
    await ui.alert(out.closedShort.message || "Set closed short.");
    return { done: true, result: RELOAD };
  }
  if (out.cancelled) return { done: true, result: out.closedShort ? RELOAD : NO_RELOAD };
  if (out.error) {
    await ui.alert(out.closedShort ? `${out.closedShort.message}\n\nCompleting it failed: ${out.error}` : out.error);
    return { done: true, result: out.closedShort ? RELOAD : NO_RELOAD };
  }
  if (out.closedShort) await ui.alert(out.closedShort.message || "Set closed short.");
  return { done: false };
}

/**
 * Complete a task. Asks first (and on the Laser warns when fewer sheets were cut than required),
 * then the operator's PIN; a remake hold is answered on the way (postCompletion). After it
 * completes: the task report (Laser: per-part report, plus the corrected routing slips when
 * defects moved the routing), the post-pack status labels to the laser Zebra (print queue), and
 * the Laser material-deduction check. The Laser label-confirmation gate stays the caller's
 * (it opens the desktop's own modal).
 */
export async function completeTask({ task, workstation, isMaintenanceTask = false, jobId, ui, setBusy = noop }) {
  if (
    workstation === "Laser" &&
    !isMaintenanceTask &&
    task.sheet_required > 0 &&
    task.sheet_count < task.sheet_required
  ) {
    const proceed = await ui.confirm(
      `WARNING: You have cut only ${task.sheet_count} of ${task.sheet_required} sheets. Complete anyway?`,
      { title: "Not every sheet is cut", confirmLabel: "Complete anyway", danger: true }
    );
    if (!proceed) return NO_RELOAD;
  }

  if (!(await ui.confirm("Complete this task?", { title: "Complete", confirmLabel: "Complete" }))) return NO_RELOAD;

  const userPin = await ui.pin(OPERATOR_PIN);
  if (!userPin) return NO_RELOAD;

  // The saw records which stick it cut (2026-10-14): its heat goes with the parts and every tail.
  if (!(await recordStickLotIfAsked({ task, pin: userPin, ui }))) return NO_RELOAD;

  setBusy(true);
  try {
    const out = await postCompletion(`${API_BASE}/tasks/${task.task_id}/complete`,
      { pin: userPin, punch_time_utc: new Date().toISOString() }, {}, ui);
    const settled = await settleCompletionOutcome(out, ui);
    if (settled.done) return settled.result;

    const completeData = await out.res.json();
    const surplusNote = surplusCompleteNote(completeData.surplus_bundles);
    if (completeData.auto_voided) {
      await ui.alert(
        `Task completed.\n\n⚠️ WARNING: ${completeData.auto_voided.count} downstream task(s) ` +
        `were automatically voided because this task completed with 0 quantity ` +
        `(all parts defected out).` + surplusNote
      );
    } else {
      await ui.alert("Task completed." + surplusNote);
    }

    // Laser with a packaging plan: the routing slip printed at Start; at Complete only the per-part
    // Task Report — unless defects moved the routing (placements_changed), then both.
    const isLaserWithRouting = workstation === "Laser" && !isMaintenanceTask && completeData.packaging_routing;
    const includeOverride = isLaserWithRouting
      ? (completeData.packaging_routing.placements_changed === true ? "all" : "report")
      : null;
    await printTaskReport({ task, jobId, isMaintenanceTask, includeOverride, ui });

    if (workstation === "Laser" && !isMaintenanceTask) {
      // Post-pack status labels to the laser Zebra (print queue → jobq-print-agent). The API caps
      // max_retries=1 for this kind; the agent falls back to the .epl stream inside that attempt.
      try {
        const labelRes = await postJson(`${API_BASE}/print/queue`, {
          station: "laser",
          kind: "post_pack_labels",
          pdf_url: `/tasks/${task.task_id}/post_pack_labels.pdf`,
          task_id: task.task_id,
          params: { trigger: "task_complete" },
        });
        if (!labelRes.ok) console.warn("Post-pack labels enqueue non-OK:", labelRes.status);
      } catch (e) {
        console.error("Network error queuing post-pack labels:", e);
      }

      try {
        const verifyRes = await fetch(`${API_BASE}/tasks/${task.task_id}/verify_material_deduction`);
        if (verifyRes.ok) {
          const v = await verifyRes.json();
          if (v.deduction_ok) {
            await ui.alert("✅ Material deduction recorded successfully.");
          } else {
            await ui.alert(
              "⚠️ Material deduction FAILED! Inventory may be incorrect. " +
              "A Slack alert has been sent."
            );
          }
        } else {
          console.error(await verifyRes.text());
          await ui.alert("Could not verify material deduction (server error).");
        }
      } catch (e) {
        console.error(e);
        await ui.alert("Could not verify material deduction (network error).");
      }
    }
    return { reload: true, travelerUrl: travelerUrlFor({ task, jobId, isMaintenanceTask, includeOverride }) };
  } catch (e) {
    await ui.alert(e.message || "Error completing task.");
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

/**
 * Before a saw / cut-list task completes: ask which stick lot it cut, unless the task already has
 * one recorded or the stick material has no lot to pick (GET /tasks/{id}/stick_lots). Resolves
 * true to go on completing, false when the operator cancelled or the lot could not be saved.
 * Fails open: an unreadable answer, or a screen whose `ui` has no picker, completes as before.
 */
export async function recordStickLotIfAsked({ task, pin, ui }) {
  if (!isSawStickTask(task) || typeof ui.stickLots !== "function") return true;
  let state;
  try {
    const r = await fetchStickLots(task.task_id);
    if (!r.ok || !r.body) return true;
    state = r.body;
  } catch {
    return true;
  }
  if (!shouldAskStickLot(state)) return true;
  const choice = await ui.stickLots(state);
  if (!choice) return false;
  if (choice.noTag || !Array.isArray(choice.lots) || !choice.lots.length) return true;
  let saved = await saveStickLots(task.task_id, { pin, lots: choice.lots });
  if (!saved.ok && saved.status === 400) {
    const msg = errorTextToMessage(JSON.stringify(saved.body || {}));
    if (isConfirmableSheetRefusal(msg)
        && await ui.confirm(msg, { title: "That lot shows no sticks left", confirmLabel: "Record it anyway", danger: true })) {
      saved = await saveStickLots(task.task_id, { pin, lots: choice.lots, allowDepleted: true });
    } else if (isConfirmableSheetRefusal(msg)) {
      return false;
    }
  }
  if (!saved.ok) {
    await ui.alert(`The stick lot was not saved: ${errorTextToMessage(JSON.stringify(saved.body || {})) || saved.status}. The task was not completed.`);
    return false;
  }
  return true;
}

/**
 * Auto-Complete: book the task at its estimate without a timer. A multi-op task books only its
 * current op (its 1/N share) and hands the task on unless it is the last op; say so first.
 * A T&M task asks for the real minutes (postCompletion, tmMinutes).
 */
export async function autoCompleteTask({ task, jobId, isMaintenanceTask = false, ui, setBusy = noop, beforePin = noop }) {
  const plan = autoCompletePlan(task);
  if (plan && plan.multiOp) {
    const ok = await ui.confirm(
      `Auto-complete ${plan.opLabel}?\n\n` +
      `This records ${plan.opLabel} at ${fmtSeconds(plan.bookedSeconds)} — its share ` +
      `(1/${plan.opCount}) of the task estimate — without running a timer` +
      (plan.lastOp
        ? `, and COMPLETES the task (this is the last operation).`
        : `, and hands the task to operation ${plan.nextOp}. This does NOT complete the task.`),
      { title: `Auto-complete ${plan.opLabel}`, confirmLabel: "Auto-complete" }
    );
    if (!ok) return { reload: false, cancelled: true };
  }
  beforePin();
  const userPin = await ui.pin(OPERATOR_PIN);
  if (!userPin) return NO_RELOAD;

  // A multi-op task that is not on its last op does not complete: no sticks leave stock yet.
  if ((!plan || !plan.multiOp || plan.lastOp)
      && !(await recordStickLotIfAsked({ task, pin: userPin, ui }))) return NO_RELOAD;

  setBusy(true);
  try {
    const out = await postCompletion(`${API_BASE}/tasks/${task.task_id}/auto_complete`, { pin: userPin }, { tmMinutes: true }, ui);
    const typedNote = out.typedMinutes != null ? ` (${out.typedMinutes} min typed)` : "";
    const settled = await settleCompletionOutcome(out, ui);
    if (settled.done) return settled.result;

    const autoData = await out.res.json();
    if (autoData.task_completed === false) {
      // A non-last op: the task is Paused on the next op, nothing to print yet.
      const remaining = autoData.ops_remaining;
      await ui.alert(
        `${autoData.auto_completed_op_label} auto-completed ` +
        `(${String(autoData.booked_duration || "").split(".")[0]} recorded${typedNote ? `, ${out.typedMinutes} min typed` : ""}).\n\n` +
        `Next up: ${autoData.next_op_label} (${remaining} operation${remaining === 1 ? "" : "s"} remaining).\n` +
        `Total time so far: ${String(autoData.total_elapsed_time || "").split(".")[0]}`
      );
      return RELOAD;
    }
    if (autoData.auto_voided) {
      await ui.alert(
        `Task auto-completed${typedNote}.\n\n⚠️ WARNING: ${autoData.auto_voided.count} downstream task(s) ` +
        `were automatically voided because this task completed with 0 quantity ` +
        `(all parts defected out).`
      );
    } else {
      await ui.alert(`Task auto-completed${typedNote}.`);
    }
    await printTaskReport({ task, jobId, isMaintenanceTask, includeOverride: undefined, ui });
    return { reload: true, travelerUrl: travelerUrlFor({ task, jobId, isMaintenanceTask }) };
  } catch (err) {
    await ui.alert(err.message || "Error auto-completing task.");
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

// ---------------------------------------------------------------------------------------------
// Part defect (non-Laser stations)
// ---------------------------------------------------------------------------------------------

/** Defects of this part on this job in the last 14 days, as the duplicate guard lists them. */
export function recentPartDefectLines(defects, partId, now = Date.now()) {
  const existing = (Array.isArray(defects) ? defects : [])
    .filter((d) => d.part_id === partId && (d.defect_type || "part") === "part")
    .filter((d) => (now - new Date(d.created_at).getTime()) < 14 * 86400 * 1000);
  return existing.slice(0, 4).map((d) => {
    const hrs = Math.round((now - new Date(d.created_at).getTime()) / 3600000);
    const age = hrs < 48 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
    return `• ${d.defective_quantity} pc(s) by ${d.recorded_by_name || "?"} ${age} — "${(d.defect_reason || "").slice(0, 60)}"`;
  });
}

/**
 * Record defective pieces of one part on a (non-Laser) task: POST /defects. First a duplicate
 * guard (the same damage may already be recorded: decoupled assembly defects don't show on task
 * rows; SO-1102 got the same 7 parts entered twice on 2026-08-05), then qty, then the reason
 * (a T&M job: the neutral cause, required), then the PIN. A loss that holds back a join set
 * answers with remake_hold_preview, shown as a heads-up.
 *
 * `isTmJob`: true / false from the queue row (utils/tmDefects queueRowSaysTm), or undefined to
 * decide from `job` (isTmLinked). The API decides whether the loss cascades downstream; nothing
 * the screen sends changes that (the phone's old `cascade: true` was never read).
 */
export async function recordPartDefect({ job, task, part, isTmJob, ui }) {
  try {
    const dRes = await fetch(`${API_BASE}/defects?job_id=${job.job_id}`);
    if (dRes.ok) {
      const lines = recentPartDefectLines(await dRes.json(), part.part_id);
      if (lines.length > 0) {
        const goOn = await ui.confirm(
          `⚠ DEFECTS ALREADY RECORDED for ${part.product_id} on this job:\n\n${lines.join("\n")}\n\n` +
          `If you're reporting the SAME damage, press Cancel — it's already in the system.\n` +
          `Press OK only to record NEW, additional damage.`,
          { title: "Already recorded?", confirmLabel: "OK" }
        );
        if (!goOn) return NO_RELOAD;
      }
    }
  } catch (e) { /* the guard is best-effort: never block defect recording */ }

  const qtyStr = await ui.prompt(`How many defective PIECES for product: ${part.product_id}?`, "1");
  if (!qtyStr) return NO_RELOAD;
  const qty = parseInt(qtyStr, 10) || 0;
  if (qty < 1) return NO_RELOAD;

  const tmJob = isTmJob === undefined ? isTmLinked(job) : isTmJob;
  const tmCause = tmJob ? await ui.causePrompt({ subject: `${part.product_id}: ${qty} pc` }) : null;
  if (tmJob && !tmCause) return NO_RELOAD;
  const reason = tmCause ? tmCause.reason : await ui.prompt("Reason for defect?", "Damaged part");
  if (!reason) return NO_RELOAD;

  const userPin = await ui.pin(OPERATOR_PIN);
  if (!userPin) return NO_RELOAD;

  const payload = {
    job_id: job.job_id,
    task_id: task.task_id,
    workstation: task.workstation,
    defect_reason: reason,
    defective_quantity: qty,
    defect_type: "part",
    pin: userPin,
    part_id: part.part_id,
  };
  try {
    const res = await postJson(`${API_BASE}/defects`, withCause(payload, tmCause));
    if (!res.ok) {
      await ui.alert(`Error reporting defect: ${await res.text()}`);
      return NO_RELOAD;
    }
    // Heads-up (jobq-api remake_hold_preview, 2026-09-26): what this loss holds back at the join.
    const defectJson = await res.json().catch(() => null);
    if (defectJson && defectJson.remake_hold_preview) {
      await ui.remakeHeadsUp(defectJson.remake_hold_preview, { lead: `Defected ${qty} from product ${part.product_id}.` });
    } else {
      await ui.alert(`Defected ${qty} from product ${part.product_id}`);
    }
    return RELOAD;
  } catch (err) {
    await ui.alert(err.message);
    return NO_RELOAD;
  }
}

// ---------------------------------------------------------------------------------------------
// Station (Laser) remote controls
// ---------------------------------------------------------------------------------------------

/** Remote E-stop of a station (the Laser). PIN first, then "are you sure". */
export async function emergencyStop({ station, ui, setBusy = noop, reason = "Mobile Remote Stop" }) {
  const pin = await ui.pin("🚨 EMERGENCY STOP 🚨\n\nEnter PIN to confirm REMOTE STOP:");
  if (!pin) return NO_RELOAD;
  if (!(await ui.confirm("ARE YOU SURE? This will halt the Laser immediately.", { title: "Emergency stop", confirmLabel: "STOP the Laser", danger: true }))) return NO_RELOAD;
  setBusy(true);
  try {
    const res = await postJson(`${API_BASE}/stations/${station}/emergency_stop`, { pin, reason });
    if (!res.ok) {
      await ui.alert(`Failed to Trigger E-Stop: ${await res.text()}`);
      return NO_RELOAD;
    }
    await ui.alert("🚨 E-STOP TRIGGERED. SYSTEM HALTED. 🚨");
    return RELOAD;
  } catch (err) {
    await ui.alert(`Network Error: ${err.message}`);
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}

export async function resetEmergencyStop({ station, ui, setBusy = noop }) {
  const pin = await ui.pin("Enter PIN to RESET System:");
  if (!pin) return NO_RELOAD;
  setBusy(true);
  try {
    const res = await postJson(`${API_BASE}/stations/${station}/reset_estop`, { pin });
    if (!res.ok) {
      await ui.alert(`Failed to Reset: ${await res.text()}`);
      return NO_RELOAD;
    }
    await ui.alert("✅ System Reset. Relays closed.");
    return RELOAD;
  } catch (err) {
    await ui.alert(`Network Error: ${err.message}`);
    return NO_RELOAD;
  } finally {
    setBusy(false);
  }
}
